import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CarRental } from '../models';

export class CarRentalService {
    private static getCollectionPath(tripId: string) {
        return `trips/${tripId}/carRentals`;
    }

    private static convertTimestamps(data: any): any {
        const converted = { ...data };
        if (data.pickupDate instanceof Timestamp) {
            converted.pickupDate = data.pickupDate.toDate();
        }
        if (data.returnDate instanceof Timestamp) {
            converted.returnDate = data.returnDate.toDate();
        }
        if (data.createdAt instanceof Timestamp) {
            converted.createdAt = data.createdAt.toDate();
        }
        if (data.updatedAt instanceof Timestamp) {
            converted.updatedAt = data.updatedAt.toDate();
        }
        return converted;
    }

    // Get all car rentals for a trip
    static async getCarRentals(tripId: string): Promise<CarRental[]> {
        try {
            console.log('🔍 [CarRentalService] Getting car rentals for trip:', tripId);
            const rentalsRef = collection(db, this.getCollectionPath(tripId));
            const snapshot = await getDocs(rentalsRef);

            console.log('✅ [CarRentalService] Found', snapshot.size, 'car rentals');
            const rentals = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    tripId,
                    ...this.convertTimestamps(data),
                } as CarRental;
            });

            // Sort by pickup date
            rentals.sort((a, b) => {
                const dateA = a.pickupDate ? new Date(a.pickupDate).getTime() : 0;
                const dateB = b.pickupDate ? new Date(b.pickupDate).getTime() : 0;
                return dateA - dateB;
            });

            return rentals;
        } catch (error) {
            console.error('❌ [CarRentalService] Error getting car rentals:', error);
            throw error;
        }
    }

    // Subscribe to car rentals
    static subscribeToCarRentals(
        tripId: string,
        callback: (rentals: CarRental[]) => void
    ): () => void {
        console.log('🔍 [CarRentalService] Setting up car rental subscription for trip:', tripId);
        const rentalsRef = collection(db, this.getCollectionPath(tripId));

        return onSnapshot(
            rentalsRef,
            (snapshot) => {
                console.log('📡 [CarRentalService] Car rental update:', snapshot.size, 'rentals');
                const rentals = snapshot.docs.map((doc) => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        tripId,
                        ...this.convertTimestamps(data),
                    } as CarRental;
                });

                // Sort by pickup date
                rentals.sort((a, b) => {
                    const dateA = a.pickupDate ? new Date(a.pickupDate).getTime() : 0;
                    const dateB = b.pickupDate ? new Date(b.pickupDate).getTime() : 0;
                    return dateA - dateB;
                });

                callback(rentals);
            },
            (error) => {
                console.error('❌ [CarRentalService] Subscription error:', error);
            }
        );
    }

    // Add new car rental
    static async addCarRental(
        tripId: string,
        rentalData: Omit<CarRental, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
    ): Promise<string> {
        try {
            console.log('➕ [CarRentalService] Adding car rental:', rentalData.company);
            const rentalsRef = collection(db, this.getCollectionPath(tripId));

            const now = new Date();
            const rawData = {
                ...rentalData,
                tripId,
                pickupDate: Timestamp.fromDate(rentalData.pickupDate),
                returnDate: Timestamp.fromDate(rentalData.returnDate),
                createdAt: Timestamp.fromDate(now),
                updatedAt: Timestamp.fromDate(now),
            };
            // Firestore rejects undefined values — strip them before writing
            const cleanData = Object.fromEntries(
                Object.entries(rawData).filter(([, v]) => v !== undefined)
            );
            const docRef = await addDoc(rentalsRef, cleanData);

            console.log('✅ [CarRentalService] Car rental added with ID:', docRef.id);
            return docRef.id;
        } catch (error) {
            console.error('❌ [CarRentalService] Error adding car rental:', error);
            throw error;
        }
    }

    // Update car rental
    static async updateCarRental(
        tripId: string,
        rentalId: string,
        rentalData: Partial<Omit<CarRental, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>>
    ): Promise<void> {
        try {
            console.log('✏️ [CarRentalService] Updating car rental:', rentalId);
            const rentalRef = doc(db, this.getCollectionPath(tripId), rentalId);

            const rawUpdate: any = {
                ...rentalData,
                updatedAt: Timestamp.fromDate(new Date()),
            };
            // Firestore rejects undefined values — strip them before writing
            const updateData: any = Object.fromEntries(
                Object.entries(rawUpdate).filter(([, v]) => v !== undefined)
            );

            if (rentalData.pickupDate) {
                updateData.pickupDate = Timestamp.fromDate(rentalData.pickupDate);
            }
            if (rentalData.returnDate) {
                updateData.returnDate = Timestamp.fromDate(rentalData.returnDate);
            }

            await updateDoc(rentalRef, updateData);
            console.log('✅ [CarRentalService] Car rental updated');
        } catch (error) {
            console.error('❌ [CarRentalService] Error updating car rental:', error);
            throw error;
        }
    }

    // Delete car rental
    static async deleteCarRental(tripId: string, rentalId: string): Promise<void> {
        try {
            console.log('🗑️ [CarRentalService] Deleting car rental:', rentalId);
            const rentalRef = doc(db, this.getCollectionPath(tripId), rentalId);
            await deleteDoc(rentalRef);
            console.log('✅ [CarRentalService] Car rental deleted');
        } catch (error) {
            console.error('❌ [CarRentalService] Error deleting car rental:', error);
            throw error;
        }
    }
}
